import type {
  PoseData,
  PersonID,
  PersonData,
  PersonCollection as IPersonCollection,
  MultiPersonConfig,
  GestureState,
  MotionTracker,
} from '../../types';
import { PersonMatcher } from './PersonMatcher';
import { PersonIDGenerator } from './PersonIDGenerator';
import { VisualizationAssigner } from './VisualizationAssigner';
import { PersonCollection } from './PersonCollection';

/**
 * PersonTracker manages the tracking of multiple people across frames.
 * 
 * Responsibilities:
 * - Match detected poses to existing Person_IDs across frames
 * - Assign new Person_IDs to unmatched poses
 * - Remove Person_IDs after timeout (30 frames without match)
 * - Maintain PersonCollection data structure
 * - Calculate Z-depth for occlusion ordering
 * 
 * Algorithm:
 * 1. Match detected poses to existing people using PersonMatcher
 * 2. Update matched people with new pose data and lastSeenFrame
 * 3. Create new PersonData for unmatched poses
 * 4. Remove people that haven't been seen for timeoutFrames
 * 
 * Validates Requirements 1.3, 1.4, 1.5, 2.1, 2.2, 2.3
 */
export class PersonTracker {
  private collection: PersonCollection;
  private matcher: PersonMatcher;
  private idGenerator: PersonIDGenerator;
  private visualAssigner: VisualizationAssigner;
  private readonly config: MultiPersonConfig;

  /**
   * Create a PersonTracker with specified configuration.
   * 
   * @param config Multi-person tracking configuration
   */
  constructor(config: MultiPersonConfig) {
    this.config = config;
    this.collection = new PersonCollection();
    this.matcher = new PersonMatcher(config.matchingThreshold);
    this.idGenerator = new PersonIDGenerator();
    this.visualAssigner = new VisualizationAssigner();
  }

  /**
   * Update tracking with new frame of detected poses.
   * 
   * Process:
   * 1. Match detected poses to existing people
   * 2. Update matched people with new pose data
   * 3. Create new people for unmatched poses
   * 4. Remove people that have timed out
   * 
   * @param poses Array of detected poses from current frame
   * @param frameNumber Current frame number
   * @returns Updated PersonCollection
   * 
   * Validates Requirements 1.3, 1.4, 1.5, 2.1, 2.2, 2.3
   */
  update(poses: PoseData[], frameNumber: number): IPersonCollection {
    // Update frame count
    this.collection.setFrameCount(frameNumber);

    // Match detected poses to existing people
    const matchResult = this.matcher.matchPoses(
      poses,
      this.collection.toInterface().people
    );

    // Update matched people
    for (const match of matchResult.matches) {
      const existingPerson = this.collection.get(match.personId);
      if (existingPerson) {
        const updatedPerson: PersonData = {
          ...existingPerson,
          poseData: match.pose,
          lastSeenFrame: frameNumber,
          zDepth: this.calculateZDepth(match.pose),
        };
        this.collection.update(updatedPerson);
      }
    }

    // Create new people for unmatched poses
    for (const pose of matchResult.unmatchedPoses) {
      const newPerson = this.createNewPerson(pose, frameNumber);
      this.collection.add(newPerson);
    }

    // Remove people that have timed out
    this.removeTimedOutPeople(frameNumber);

    return this.collection.toInterface();
  }

  /**
   * Create a new PersonData for a detected pose.
   * Assigns unique ID, random visualization, and initializes state.
   * 
   * @param pose Detected pose data
   * @param frameNumber Current frame number
   * @returns New PersonData instance
   * 
   * Validates Requirements 1.3, 2.2, 3.1, 3.2
   */
  private createNewPerson(pose: PoseData, frameNumber: number): PersonData {
    const personId = this.idGenerator.generateID();
    const visualConfig = this.visualAssigner.createVisualConfig();
    const visualizationType = this.visualAssigner.assignVisualizationType();

    return {
      personId,
      poseData: pose,
      visualConfig,
      gestureState: this.createInitialGestureState(),
      trailBuffer: [],
      motionTracker: this.createInitialMotionTracker(pose),
      zDepth: this.calculateZDepth(pose),
      lastSeenFrame: frameNumber,
      visualizationType,
    };
  }

  /**
   * Create initial gesture state for a new person.
   * 
   * @returns Initial GestureState with empty sets
   */
  private createInitialGestureState(): GestureState {
    return {
      activeGestures: new Set(),
      lastGestureTimes: new Map(),
    };
  }

  /**
   * Create initial motion tracker for a new person.
   * Uses the person's initial pose position.
   * 
   * @param pose Initial pose data
   * @returns Initial MotionTracker
   */
  private createInitialMotionTracker(pose: PoseData): MotionTracker {
    // Use nose position as reference point for motion tracking
    const nose = pose.landmarks.nose;
    
    return {
      velocity: { x: 0, y: 0 },
      acceleration: { x: 0, y: 0 },
      lastPosition: { x: nose.x, y: nose.y },
    };
  }

  /**
   * Calculate average Z-depth for a person using torso landmarks.
   * Uses shoulders and hips with visibility filtering.
   * 
   * @param pose Pose data containing landmarks
   * @returns Average Z-depth value
   * 
   * Validates Requirements 6.1, 6.2
   */
  private calculateZDepth(pose: PoseData): number {
    const torsoLandmarks = [
      pose.landmarks.leftShoulder,
      pose.landmarks.rightShoulder,
      pose.landmarks.leftHip,
      pose.landmarks.rightHip,
    ];

    // Filter out low-visibility landmarks
    const validLandmarks = torsoLandmarks.filter(lm => lm.visibility > 0.5);

    if (validLandmarks.length === 0) {
      return 0; // Fallback if no valid torso landmarks
    }

    const sum = validLandmarks.reduce((acc, lm) => acc + lm.z, 0);
    return sum / validLandmarks.length;
  }

  /**
   * Remove people that haven't been seen for timeoutFrames.
   * 
   * @param currentFrame Current frame number
   * 
   * Validates Requirements 1.4, 2.3
   */
  private removeTimedOutPeople(currentFrame: number): void {
    const peopleToRemove: PersonID[] = [];

    this.collection.forEach((person) => {
      const framesSinceLastSeen = currentFrame - person.lastSeenFrame;
      if (framesSinceLastSeen > this.config.timeoutFrames) {
        peopleToRemove.push(person.personId);
      }
    });

    for (const personId of peopleToRemove) {
      this.collection.remove(personId);
    }
  }

  /**
   * Get current collection of tracked people.
   * 
   * @returns PersonCollection interface
   */
  getPeople(): IPersonCollection {
    return this.collection.toInterface();
  }

  /**
   * Reset tracking (clear all people).
   */
  reset(): void {
    this.collection.clear();
    this.collection.setFrameCount(0);
    this.idGenerator.reset();
  }

  /**
   * Get person data by ID.
   * 
   * @param personId Person identifier
   * @returns PersonData if found, undefined otherwise
   */
  getPerson(personId: PersonID): PersonData | undefined {
    return this.collection.get(personId);
  }

  /**
   * Get the current configuration.
   * 
   * @returns MultiPersonConfig
   */
  getConfig(): MultiPersonConfig {
    return { ...this.config };
  }
}
